import { Component, h, Prop, State, Listen } from "@stencil/core";
import "../../utils/i18n";
import "../../utils/global";
import { handleClick, updateLiveLocationHelper } from "../../utils/utils";

@Component({
    tag: "shared-nav-mobile-view",
    shadow: false
})

/**
 * shared-nav-mobile-view is the mobile version of the Navigation.
 * @param {Object} navBarData - the data that will be rendered in the nav
 * @param {String} brand - the current brand the nav is on (this is used for rendering the icon)
 * @param {boolean} departmentsOpenProp - whether or not the Category section should be open on initial run
 * @param {Function} clickHandler - If passed in the Nav will use it insteaf of the default href Functionality
 */
export class SharedNavMobileView {
    @Prop({ mutable: true }) navBarData: {};
    @Prop() brand: string;
    @Prop() departmentsOpenProp: boolean = false;
    @Prop() testing: boolean = false;
    @Prop() clickHandler: Function;

    @State() navBarDataUpdated: {};
    @State() currentLocation: Array<string> = [];
    @State() departmentsOpen: boolean = false;
    @State() currentDepartment: string = "";
    @State() oldDepartment: string;
    @State() mainDepartment: string;

    /**
     * Runs before the first rendering of the component.
     * This sets everything up
     */
    async componentWillLoad() {
        this.updateLiveLocation();

        window.addEventListener("keydown", () => {
            this.updateLiveLocation();
        });
    }

    /**
     * This gets called before a rerender
     */
    async componentDidLoad() {
        if (this.departmentsOpenProp !== this.departmentsOpen) {
            this.toggleCategoriesSection();
        }
    }

    async componentWillUpdate() {
        this.updateLiveLocation(true, true);
    }

    /**
     * Closes the shared nav if the user clicks out of the shared nav
     * @param event The click event
     */
    @Listen("click", { target: "window" })
    async checkCloseRequirements(event) {
        if (!this.testing) {
            const element = document
                .querySelector("#shared-nav-root")
                .shadowRoot.querySelector("#usfsn-mobile-nav-section");
            if (!element.contains(event.target)) {
                this.toggleCategoriesSection(true);
            }
        }
    }

    /**
     * Sets the State variable sthat tell the nav what the current site is.
     * This is used to show a live location to the user
     */
    async updateLiveLocation(onlyMain: boolean = false, onlyCurrent: boolean = false) {
        this.oldDepartment = this.currentDepartment;

        const liveLocationData = await updateLiveLocationHelper(this.navBarData);

        this.navBarDataUpdated = liveLocationData.navBarData;

        if (onlyMain) {
            if (!onlyCurrent) {
                this.mainDepartment = this.navBarDataUpdated[this.currentDepartment].name;
            }
        } else {
            this.currentDepartment = liveLocationData.currentDepartment;
            this.mainDepartment = this.navBarDataUpdated[this.currentDepartment].name;
        }
    }

    /**
     * This updates the variables that tell the nav what the current department is
     * @param department The name of the department to change to
     */
    private changeCurrentDepartment = (department: string) => {
        this.currentDepartment = department;
    };

    /**
     * This toggles the category section open/closed
     */
    private toggleCategoriesSection = (closeAll: boolean = false) => {
        if (closeAll) {
            this.departmentsOpen = false;
        } else {
            this.departmentsOpen = !this.departmentsOpen;
        }
    };

    /**
     * This returns the correct toggle icon based on the brand and the open/closed state
     * @param open Whether or not the categories section is open
     * @returns A SVG of the icon
     */
    private getToggleIcon = (open: boolean) => {
        if (this.brand === "chevrolet") {
            if (open) {
                return (
                    <svg width="10" height="7" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M4.99967 0.0834961L9.66634 6.75016L0.333008 6.75016L4.99967 0.0834961Z"
                            fill="#262626"
                        />
                    </svg>
                );
            } else {
                return (
                    <svg width="10" height="7" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4.99967 6.75L9.66634 0.0833335L0.333008 0.0833335L4.99967 6.75Z" fill="#262626" />
                    </svg>
                );
            }
        } else if (this.brand === "cadillac") {
            if (open) {
                return (
                    <svg
                        class={"usfsn-toggle-flipped"}
                        width="16"
                        height="7"
                        viewBox="0 0 16 7"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M8.44977 0.200195H7.55977L0.00976562 5.1502L0.779766 6.2002L8.00977 1.4602L15.2298 6.2002L15.9998 5.1502L8.44977 0.200195Z"
                            fill="#282828"
                        />
                    </svg>
                );
            } else {
                return (
                    <svg width="16" height="7" viewBox="0 0 16 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M8.44977 0.200195H7.55977L0.00976562 5.1502L0.779766 6.2002L8.00977 1.4602L15.2298 6.2002L15.9998 5.1502L8.44977 0.200195Z"
                            fill="#282828"
                        />
                    </svg>
                );
            }
        } else if (this.brand === "buick") {
            if (open) {
                return (
                    <svg width="13" height="8" viewBox="0 0 13 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M2.10019 7.7002L6.7002 3.1002L11.3002 7.7002L12.7002 6.3002L6.7002 0.300195L0.700195 6.3002L2.10019 7.7002Z"
                            fill="#C9480C"
                        />
                    </svg>
                );
            } else {
                return (
                    <svg width="13" height="8" viewBox="0 0 13 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M2.10019 0.299805L6.7002 4.8998L11.3002 0.299805L12.7002 1.6998L6.7002 7.6998L0.700195 1.6998L2.10019 0.299805Z"
                            fill="#2B2B2B"
                        />
                    </svg>
                );
            }
        } else if (this.brand === "gmc") {
            if (open) {
                return (
                    <svg width="13" height="9" viewBox="0 0 13 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M2.10019 8.14673L6.7002 3.54673L11.3002 8.14673L12.7002 6.74673L6.7002 0.746728L0.700195 6.74673L2.10019 8.14673Z"
                            fill="white"
                        />
                    </svg>
                );
            } else {
                return (
                    <svg width="13" height="9" viewBox="0 0 13 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M2.10019 0.746826L6.7002 5.34683L11.3002 0.746826L12.7002 2.14683L6.7002 8.14683L0.700195 2.14683L2.10019 0.746826Z"
                            fill="white"
                        />
                    </svg>
                );
            }
        } else {
            if (open) {
                return (
                    <svg width="10" height="7" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M4.99967 0.0834961L9.66634 6.75016L0.333008 6.75016L4.99967 0.0834961Z"
                            fill="#262626"
                        />
                    </svg>
                );
            } else {
                return (
                    <svg width="10" height="7" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4.99967 6.75L9.66634 0.0833335L0.333008 0.0833335L4.99967 6.75Z" fill="#262626" />
                    </svg>
                );
            }
        }
    };

    async componentDidUpdate() {
        if (!this.testing) {
            if (this.departmentsOpen) {
                // If the department has changed we need to reset the scrolling
                if (this.oldDepartment !== this.currentDepartment) {
                    let element = document
                        .querySelector("#shared-nav-root")
                        .shadowRoot.querySelector("#usfsn-mobile-categories-container-outer");
                    element.scrollTop = 0;
                }
                let innerElement = document
                    .querySelector("#shared-nav-root")
                    .shadowRoot.querySelector("#usfsn-mobile-categories-container-outer");
                innerElement.setAttribute(
                    "style",
                    "max-height:calc(100vh - " + innerElement.getBoundingClientRect().y + "px)"
                );

                // If the window has changed its size we need to fix the styling of the height
                window.addEventListener("resize", () => {
                    innerElement = document
                        .querySelector("#shared-nav-root")
                        .shadowRoot.querySelector("#usfsn-mobile-categories-container-outer");
                    innerElement.setAttribute(
                        "style",
                        "max-height:calc(100vh - " + innerElement.getBoundingClientRect().y + "px)"
                    );
                });
            }
        }
    }

    render() {
        return (
            <div>
                {/* The script tags are for getting Analytics Tagging outside of the Shadow Root */}
                <script src="https://brands.gm-cdn.com/_gbpe/code/prod1/analytics-event-retargeting.js"></script>

                <div class={"usfsn-mobile-section-styles"}>
                    <div id={"usfsn-mobile-nav-section"} class={"usfsn-mobile-nav-section-styles"}>
                        <div id={"usfsn-mobile-nav-container"} class="gb-tab-nav-custom usfsn-mobile-gb-tab-nav">
                            {/* Departments Toggle Section */}
                            <ul
                                id={"usfsn-mobile-main-toggle-container"}
                                class="usfsn-mobile-nopadding content-button-group usfsn-mobile-custom-background-color-departments usfsn-mobile-departments-padding"
                            >
                                <li
                                    id={"usfsn-mobile-main-toggle-line"}
                                    class={"usfsn-mobile-nopadding content-button usfsn-fullwidth"}
                                >
                                    <button
                                        id={"usfsn-mobile-main-toggle-button"}
                                        class={"usfsn-mobile-content-button stat-expand-icon"}
                                        data-dtm={"nav secondary"}
                                        onClick={() => {
                                            this.toggleCategoriesSection();
                                            this.updateLiveLocation();
                                        }}
                                    >
                                        <span
                                            id={"usfsn-mobile-main-toggle-text"}
                                            class={
                                                "usfsn-typography usfsn-mobile-departments-toggle-font-styling usfsn-leftfloat"
                                            }
                                        >
                                            {this.mainDepartment}
                                        </span>{" "}
                                        {/* If the departments section is open it will enter the first span */}
                                        {this.departmentsOpen ? (
                                            <span id={"usfsn-mobile-main-toggle-open-icon"} class={"usfsn-rightfloat"}>
                                                {this.getToggleIcon(true)}
                                            </span>
                                        ) : (
                                            <span
                                                id={"usfsn-mobile-main-toggle-closed-icon"}
                                                class={"usfsn-rightfloat"}
                                            >
                                                {this.getToggleIcon(false)}
                                            </span>
                                        )}
                                    </button>
                                </li>
                            </ul>
                            {/* Departments Section inside of the Categories Panel */}
                            {this.departmentsOpen && (
                                <div
                                    id={"usfsn-mobile-content-container"}
                                    class={"usfsn-fullwidth usfsn-custom-background-color-categories"}
                                >
                                    <div
                                        id={"usfsn-mobile-departments-container"}
                                        class={"usfsn-mobile-depcat-box-child usfsn-mobile-departments-section"}
                                    >
                                        {Object.keys(this.navBarDataUpdated).map((department) => {
                                            // If the nav is on a url with the current department it will enter the if portion

                                            if (this.currentDepartment === department) {
                                                return (
                                                    <a
                                                        id={
                                                            "usfsn-mobile-department-button-" +
                                                            this.navBarDataUpdated[department].name
                                                        }
                                                        class={
                                                            "usfsn-typography usfsn-department-casing usfsn-vertical-display usfsn-mobile-department-item usfsn-mobile-item-spacing heavy usfsn-mobile-current-department-item stat-text-link usfsn-nav-no-underline usfsn-mobile-category-column-text"
                                                        }
                                                        data-dtm={"nav secondary"}
                                                        onClick={() => {
                                                            this.changeCurrentDepartment(department);
                                                        }}
                                                    >
                                                        {this.navBarDataUpdated[department].name}
                                                    </a>
                                                );
                                            } else {
                                                return (
                                                    <a
                                                        id={
                                                            "usfsn-mobile-department-button-" +
                                                            this.navBarDataUpdated[department].name
                                                        }
                                                        class={
                                                            "usfsn-typography usfsn-department-casing usfsn-vertical-display usfsn-mobile-department-item usfsn-mobile-item-spacing heavy stat-text-link usfsn-nav-no-underline usfsn-mobile-unselected-item usfsn-mobile-category-column-text"
                                                        }
                                                        data-dtm={"nav secondary"}
                                                        onClick={() => {
                                                            this.changeCurrentDepartment(department);
                                                        }}
                                                    >
                                                        {this.navBarDataUpdated[department].name}
                                                    </a>
                                                );
                                            }
                                        })}
                                    </div>
                                    {/* Categories Section */}
                                    <div
                                        id="usfsn-mobile-categories-container-outer"
                                        class="usfsn-fullwidth usfsn-mobile-vertical-scroll"
                                    >
                                        <div
                                            id={"usfsn-mobile-categories-container-inner"}
                                            class={
                                                "usfsn-mobile-depcat-box-child usfsn-mobile-categories-section usfsn-mobile-vertical-scroll"
                                            }
                                        >
                                            <a
                                                id="usfsn-department-home"
                                                class={
                                                    (Object.values(this.navBarDataUpdated)[this.currentDepartment][
                                                        "home"
                                                    ][3]
                                                        ? "usfsn-current-mobile-category-item usfsn-active-category "
                                                        : "") +
                                                    "usfsn-typography usfsn-mobile-department-home usfsn-category-column-padding"
                                                }
                                                href={
                                                    Object.values(this.navBarDataUpdated)[this.currentDepartment][
                                                        "home"
                                                    ][1]
                                                }
                                                data-dtm={String(
                                                    Object.values(
                                                        Object.values(this.navBarDataUpdated)[this.currentDepartment]
                                                    )[0]
                                                ).toLowerCase()}
                                            >
                                                {
                                                    Object.values(this.navBarDataUpdated)[this.currentDepartment][
                                                        "home"
                                                    ][0]
                                                }
                                            </a>
                                            <span
                                                class={
                                                    !Object.values(this.navBarDataUpdated)[this.currentDepartment][
                                                        "categoryHeader"
                                                    ]
                                                        ? "usfsn-mobile-category-header-gap"
                                                        : ""
                                                }
                                            ></span>
                                            {Object.values(this.navBarDataUpdated).map((contentArray, index) => {
                                                if (this.currentDepartment === String(index)) {
                                                    const dataDTMValue = String(
                                                        Object.values(
                                                            Object.values(this.navBarDataUpdated)[
                                                                this.currentDepartment
                                                            ]
                                                        )[0]
                                                    ).toLowerCase();
                                                    return Object.values(contentArray["columns"]).map((column) => {
                                                        {
                                                            if (Array.isArray(column)) {
                                                                let map = column.map((columnArray) => {
                                                                    // If the nav is on a url with the current category it will enter the if portion
                                                                    if (!Array.isArray(columnArray)) {
                                                                        if (columnArray) {
                                                                            return (
                                                                                <p
                                                                                    class={
                                                                                        "usfsn-typography usfsn-mobile-category-header usfsn-mobile-category-column-text usfsn-mobile-category-header-gap usfsn-category-column-padding"
                                                                                    }
                                                                                >
                                                                                    {columnArray}
                                                                                </p>
                                                                            );
                                                                        } else if (
                                                                            Object.values(this.navBarDataUpdated)[
                                                                                index
                                                                            ]["categoryHeader"]
                                                                        ) {
                                                                            return (
                                                                                <p
                                                                                    class={
                                                                                        "usfsn-typography usfsn-mobile-category-header usfsn-mobile-category-column-text"
                                                                                    }
                                                                                >
                                                                                    {columnArray}
                                                                                </p>
                                                                            );
                                                                        }
                                                                    } else if (columnArray[3]) {
                                                                        return (
                                                                            <a
                                                                                id="usfsn-category-button"
                                                                                onClick={(event) => {
                                                                                    handleClick(
                                                                                        event,
                                                                                        this.clickHandler,
                                                                                        columnArray[1]
                                                                                    );
                                                                                    this.toggleCategoriesSection();
                                                                                }}
                                                                                href={columnArray[1]}
                                                                                class="usfsn-typography usfsn-clickable usfsn-vertical-display usfsn-mobile-category-item usfsn-mobile-item-spacing usfsn-mobile-categories-font-size usfsn-nav-no-underline usfsn-current-mobile-category-item usfsn-active-category stat-text-link usfsn-category-column-padding  usfsn-mobile-category-column-text"
                                                                                data-dtm={
                                                                                    "nav secondary:" + dataDTMValue
                                                                                }
                                                                            >
                                                                                {columnArray[0]}
                                                                            </a>
                                                                        );
                                                                    } else {
                                                                        if (columnArray[0]) {
                                                                            return (
                                                                                <a
                                                                                    id="usfsn-category-button"
                                                                                    onClick={(event) => {
                                                                                        handleClick(
                                                                                            event,
                                                                                            this.clickHandler,
                                                                                            columnArray[1]
                                                                                        );
                                                                                        this.toggleCategoriesSection();
                                                                                    }}
                                                                                    href={columnArray[1]}
                                                                                    class="usfsn-typography usfsn-clickable usfsn-vertical-display usfsn-mobile-category-item usfsn-mobile-item-spacing usfsn-mobile-categories-font-size usfsn-nav-no-underline stat-text-link usfsn-mobile-unselected-item usfsn-category-column-padding usfsn-mobile-category-column-text"
                                                                                    data-dtm={
                                                                                        "nav secondary:" + dataDTMValue
                                                                                    }
                                                                                >
                                                                                    {columnArray[0]}
                                                                                </a>
                                                                            );
                                                                        }
                                                                    }
                                                                });
                                                                return map;
                                                            }
                                                        }
                                                    });
                                                }
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    {/* This is for the backdrop that shows up when the nav is expanded */}
                    {this.departmentsOpen && <div id="usfsn-nav-backdrop" class={"usfsn-modal-backdrop"} />}
                </div>
            </div>
        );
    }
}
